import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiClient from '../utils/axiosConfig';
import { cache, CacheKeys, CacheTTL, invalidateBookingsCache } from '../utils/cache';
import { useAuth } from '../context/AuthContext';
import AppShell from '../components/layout/AppShell';
import EmptyState from '../components/ui/EmptyState';
import { SkeletonGrid } from '../components/ui/SkeletonLoader';
import { hapticSuccess } from '../utils/haptic';
import { canRescheduleBooking, RESCHEDULE_LIMIT_MESSAGE } from '../utils/booking';
import { isBookingLimitError, getBookingLimitMessage } from '../utils/bookingLimits';
import { useBookingLimits } from '../hooks/useBookingLimits';
import BookingLimitBanner from '../components/ui/BookingLimitBanner';
import AddToCalendarButton from '../components/ui/AddToCalendarButton';
import useDocumentTitle from '../hooks/useDocumentTitle';
import '../styles/MyBookingsPage.css';

function MyBookingsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, authChecked } = useAuth();
  useDocumentTitle('حجوزاتي');
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState(null);
  const {
    can_book: canBookByLimit,
    message: bookingLimitMessage,
    daily_actions_used: dailyActionsUsed,
    daily_limit: dailyLimit,
    refreshBookingLimits,
  } = useBookingLimits(isAuthenticated);

  const fetchBookings = useCallback(() => {
    if (!isAuthenticated) {
      setBookings([]);
      setLoading(false);
      return;
    }
    const cached = cache.get(CacheKeys.bookingsAll);
    if (cached) {
      setBookings(cached);
      setLoading(false);
      return;
    }
    setLoading(true);
    apiClient
      .get('/bookings/all')
      .then((res) => {
        if (res.data?.success) {
          setBookings(res.data.bookings || []);
          cache.set(CacheKeys.bookingsAll, res.data.bookings || [], CacheTTL.bookings);
        } else {
          setBookings([]);
        }
      })
      .catch(() => setBookings([]))
      .finally(() => setLoading(false));
  }, [isAuthenticated]);

  useEffect(() => {
    if (!authChecked) return;
    if (!isAuthenticated) {
      sessionStorage.setItem('loginReturnTo', JSON.stringify({ pathname: '/bookings', search: '' }));
      navigate('/login');
      return;
    }
    fetchBookings();
  }, [authChecked, isAuthenticated, fetchBookings, navigate]);

  const handleDelete = (bookingId) => {
    if (!bookingId || actionId) return;
    if (!canBookByLimit) {
      toast.info(bookingLimitMessage);
      return;
    }
    if (!window.confirm('هل أنت متأكد من إلغاء هذا الحجز؟')) return;
    setActionId(bookingId);
    apiClient
      .delete(`/bookings/${bookingId}?businessid=${encodeURIComponent(bookings.find((b) => b.id === bookingId)?.account_id || '')}`)
      .then((res) => {
        if (res.data?.success) {
          toast.success(res.data.message || 'تم إلغاء الحجز');
          hapticSuccess();
          invalidateBookingsCache();
          refreshBookingLimits();
          fetchBookings();
        } else {
          toast.error(res.data.message || 'فشل إلغاء الحجز');
        }
      })
      .catch((err) => {
        if (isBookingLimitError(err)) {
          refreshBookingLimits();
          toast.info(getBookingLimitMessage(err));
          return;
        }
        toast.error(err.response?.data?.message || 'فشل إلغاء الحجز');
      })
      .finally(() => setActionId(null));
  };

  const handleReschedule = (booking) => {
    if (!canBookByLimit) {
      toast.info(bookingLimitMessage);
      return;
    }
    if (!canRescheduleBooking(booking)) {
      toast.error(RESCHEDULE_LIMIT_MESSAGE);
      return;
    }
    sessionStorage.setItem('selectedBookingType', String(booking.reservation_type_id));
    sessionStorage.setItem('selectedBookingTypeName', booking.booking_type || '');
    navigate(
      `/calendar?businessid=${encodeURIComponent(booking.account_id)}&reschedule=${booking.id}`
    );
  };

  if (!authChecked || loading) {
    return (
      <AppShell>
        <div className="page-container my-bookings-page">
          <h1 className="my-bookings-page-title">حجوزاتي</h1>
          <SkeletonGrid count={3} />
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="page-container my-bookings-page">
        <h1 className="my-bookings-page-title">حجوزاتي</h1>
        <p className="my-bookings-page-sub">جميع مواعيدك القادمة في مكان واحد</p>

        {!canBookByLimit && (
          <BookingLimitBanner
            message={bookingLimitMessage}
            dailyActionsUsed={dailyActionsUsed}
            dailyLimit={dailyLimit}
          />
        )}

        {bookings.length === 0 ? (
          <EmptyState
            icon="📅"
            title="لا توجد حجوزات قادمة"
            message="احجز موعدك الآن في أحد الصالونات المتاحة"
            actionLabel="تصفح الصالونات"
            onAction={() => navigate('/')}
          />
        ) : (
          <ul className="my-bookings-page-list" role="list">
            {bookings.map((b) => (
              <li key={b.id} className="my-bookings-page-item">
                <div className="my-bookings-page-item-main">
                  <Link
                    to={`/?businessid=${encodeURIComponent(b.account_id)}`}
                    className="my-bookings-page-salon"
                  >
                    {b.account_title || 'صالون'}
                  </Link>
                  <span className="my-bookings-page-type">{b.booking_type || 'حجز'}</span>
                  <span className="my-bookings-page-datetime">{b.date} — {b.time}</span>
                </div>
                <div className="my-bookings-page-actions">
                  <AddToCalendarButton booking={b} compact />
                  {!canBookByLimit ? null : (
                    <>
                      {canRescheduleBooking(b) && (
                        <button
                          type="button"
                          className="my-bookings-reschedule"
                          onClick={() => handleReschedule(b)}
                          disabled={actionId !== null}
                        >
                          تغيير الموعد
                        </button>
                      )}
                      <button
                        type="button"
                        className="my-bookings-delete"
                        onClick={() => handleDelete(b.id)}
                        disabled={actionId !== null}
                      >
                        {actionId === b.id ? '...' : 'إلغاء'}
                      </button>
                    </>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </AppShell>
  );
}

export default MyBookingsPage;
